import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Autocomplete, CircularProgress, TextField, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'

import { BigWidgetWrapper } from '../../../components/Widget'
import { useAppSelector } from '../../../store/hooks'
import { MoneyValue } from '../MoneyValue'

import { CommissionComparisonService } from './CommissionComparisonService'
import { CommissionStats } from './commissionComparisonTypes'

const StatBlock: React.FC<{
  label: string
  value: string | number
  isMoney?: boolean
  currency?: string | null
}> = ({ label, value, isMoney, currency }) => {
  const { palette } = useTheme()

  return (
    <Stack spacing={0.5} minWidth={120} flex={1}>
      <Typography variant="caption" color={palette.grey[700]} fontWeight={600}>
        {label}
      </Typography>
      {isMoney ? (
        <MoneyValue money={Number(value ?? 0).toFixed(2)} currency={currency || undefined} />
      ) : (
        <Typography fontWeight={700}>{value}</Typography>
      )}
    </Stack>
  )
}

export const CommissionCompareWidget: React.FC = () => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()

  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  const [symbols, setSymbols] = useState<string[]>([])
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null)
  const [stats, setStats] = useState<CommissionStats | null>(null)
  const [symbolsLoading, setSymbolsLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatNumber = useCallback(
    (value: number, fractionDigits = 2) =>
      Number(value ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    [],
  )

  useEffect(() => {
    if (!currentAccount?.id) {
      setSymbols([])
      setSelectedSymbol(null)
      setStats(null)
      return
    }

    const loadSymbols = async () => {
      setSymbolsLoading(true)
      setSelectedSymbol(null)
      setStats(null)
      setError(null)

      try {
        const { data } = await CommissionComparisonService.getSymbols(currentAccount.id)
        setSymbols(data?.symbols || [])
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
      } finally {
        setSymbolsLoading(false)
      }
    }

    loadSymbols()
  }, [currentAccount?.id])

  useEffect(() => {
    if (!selectedSymbol || !currentAccount?.id) {
      return
    }

    const loadStats = async () => {
      setStatsLoading(true)
      setError(null)

      try {
        const { data } = await CommissionComparisonService.getCommissionStats(
          currentAccount.id,
          selectedSymbol,
        )

        setStats(data)
      } catch (err) {
        setStats(null)
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
      } finally {
        setStatsLoading(false)
      }
    }

    loadStats()
  }, [currentAccount?.id, selectedSymbol])

  const statContent = useMemo(() => {
    if (statsLoading) {
      return <CircularProgress size={28} />
    }

    if (error) {
      return (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      )
    }

    if (!selectedSymbol) {
      return (
        <Typography color="text.secondary" textAlign="center">
          {t('selectSymbolToSeeStats', 'Select a symbol to see commission stats')}
        </Typography>
      )
    }

    if (!stats) {
      return (
        <Typography color="text.secondary" textAlign="center">
          {t('noCommissionStats', 'No commission stats available for this symbol')}
        </Typography>
      )
    }

    return (
      <Box display="grid" gap={spacing(1.5)}>
        <StatBlock
          label={t('avgCommissionPerLot', 'Your Avg Commission Per Lot')}
          value={stats.avgCommissionPerLot}
          isMoney
          currency={currentAccount?.currency}
        />
        <StatBlock
          label={t('avgCommissionPerTrade', 'Your Avg Commission Per Trade')}
          value={stats.avgCommissionPerTrade}
          isMoney
          currency={currentAccount?.currency}
        />
        <StatBlock
          label={t('totalCommissionPaid', 'Total Commission Paid')}
          value={stats.totalCommission}
          isMoney
          currency={currentAccount?.currency}
        />
        <StatBlock
          label={t('totalLotsTraded', 'Total Lots Traded')}
          value={formatNumber(stats.totalLots)}
        />
        <StatBlock
          label={t('totalTrades', 'Total Trades')}
          value={formatNumber(stats.totalTrades, 0)}
        />
      </Box>
    )
  }, [
    currentAccount?.currency,
    error,
    formatNumber,
    selectedSymbol,
    spacing,
    stats,
    statsLoading,
    t,
  ])

  return (
    <BigWidgetWrapper
      bgColor={palette.background.paper}
      title={t('commissionCompareWidget', 'Commission Comparison')}
      explanation={t(
        'commissionCompareExplanation',
        'Review how much commission you pay per symbol to benchmark future broker comparisons. To compare brokers fairly, we analyze only trades without overnight swaps, because swap depends on holding time and market rates, not broker efficiency.',
      )}
      sx={{ alignItems: 'flex-start' }}
    >
      <Stack spacing={spacing(2)} p={1} flex={1} justifyContent="space-between">
        <Autocomplete
          disablePortal
          options={symbols}
          value={selectedSymbol}
          loading={symbolsLoading}
          onChange={(_, value) => setSelectedSymbol(value)}
          noOptionsText={t('noSymbolsFound', 'No traded symbols yet')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('symbol', 'Symbol')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {symbolsLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ maxWidth: spacing(40) }}
        />
        <Box
          flex={1}
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
          width="100%"
        >
          {statContent}
        </Box>
      </Stack>
    </BigWidgetWrapper>
  )
}
